import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  getProjects, 
  createProject, 
  getProjectDetail, 
  createFile,
  type Project, 
  type ProjectFile 
} from '../../../lib/apis_vibe';

import { uploadAvatarGen } from '../../../lib/api';
import { useAuth } from '../../../contexts/AuthContext';
import { useCoursePermission, VIBE_CODING_COURSE_ID } from '../../../hooks/useCoursePermission';

import ProjectSidebar from './components/ProjectSidebar';
import FileGrid from './components/FileGrid';
import CreateModal from './components/CreateModal';
import Workspace from './components/Workspace';
import logo from '../../../assets/imgs/logo.png';

// ... (AvatarModal 代码保持不变，省略以节省篇幅，请保留您原来的 AvatarModal 代码) ...
interface AvatarModalProps {
  isOpen: boolean;
  onClose: () => void;
}
const AvatarModal: React.FC<AvatarModalProps> = ({ isOpen, onClose }) => {
  const { user, updateUserAvatar } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [generatedUrl, setGeneratedUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFile(null);
      setPreview(null);
      setGeneratedUrl(null);
      setIsProcessing(false);
    }
  }, [isOpen]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const f = e.target.files[0];
      setFile(f);
      setPreview(URL.createObjectURL(f));
    }
  };

  const handleUploadAndGen = async () => {
    if (!file) return;
    setIsProcessing(true);
    try {
      const res = await uploadAvatarGen(file);
      
      // 检查是否为type:error消息
      if (res.data.type === 'error') {
        const errorMessage = res.data.content || 'AI 生成失败';
        alert(`错误：${errorMessage}`);
        return;
      }
      
      const updatedUser = res.data;
      if (updatedUser.avatar_url) {
        setGeneratedUrl(updatedUser.avatar_url);
        updateUserAvatar(updatedUser.avatar_url);
      }
    } catch (err: any) {
      console.error("Avatar Gen Failed:", err);
      // 检查错误响应中是否包含type:error消息
      if (err.response && err.response.data && err.response.data.type === 'error') {
        const errorMessage = err.response.data.content || 'AI 生成失败';
        alert(`错误：${errorMessage}`);
      } else {
        alert("AI 生成失败，请稍后重试");
      }
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div className="relative w-[400px] overflow-hidden rounded-2xl border border-white/20 bg-[#1a1a1a]/90 p-6 shadow-[0_0_50px_rgba(0,243,255,0.2)]">
        <button onClick={onClose} className="absolute right-4 top-4 text-gray-400 hover:text-white">✕</button>
        <h2 className="mb-6 text-center text-xl font-bold tracking-wider text-transparent bg-clip-text bg-gradient-to-r from-[#00F3FF] to-[#BC13FE]">
          PIXAR IDENTITY LAB
        </h2>
        <div className="relative mx-auto mb-6 h-48 w-48 overflow-hidden rounded-full border-2 border-dashed border-gray-600 bg-black/50 group">
          {isProcessing && <div className="absolute inset-0 z-20 animate-scan bg-gradient-to-b from-transparent via-[#00F3FF]/20 to-transparent" />}
          {generatedUrl ? (
            <img src={generatedUrl} alt="Pixar Avatar" className="h-full w-full object-cover animate-fade-in" />
          ) : preview ? (
            <img src={preview} alt="Preview" className="h-full w-full object-cover opacity-70" />
          ) : (
            <div onClick={() => fileInputRef.current?.click()} className="flex h-full cursor-pointer flex-col items-center justify-center text-gray-500 transition-colors hover:text-white">
              <span className="text-4xl mb-2">📸</span>
              <span className="text-xs uppercase">Upload Selfie</span>
            </div>
          )}
          <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
        </div>
        <div className="flex flex-col gap-3">
          {!generatedUrl && !isProcessing && (
            <button onClick={handleUploadAndGen} disabled={!file} className={`group relative overflow-hidden rounded-lg py-3 font-bold uppercase tracking-widest transition-all ${file ? 'bg-[#00F3FF] text-black hover:shadow-[0_0_20px_#00F3FF]' : 'bg-gray-800 text-gray-500 cursor-not-allowed'}`}>
              <span className="relative z-10">Initialize Transformation</span>
            </button>
          )}
          {isProcessing && (
            <div className="flex items-center justify-center gap-2 text-[#00F3FF] font-mono text-sm animate-pulse">
              <span>AI PROCESSING</span><span className="loading-dots">...</span>
            </div>
          )}
          {generatedUrl && (
            <button onClick={onClose} className="w-full rounded-lg border border-white/20 bg-white/5 py-3 text-white hover:bg-white/10">Confirm Identity</button>
          )}
        </div>
      </div>
    </div>
  );
};

const VibeCodingPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const permission = useCoursePermission(VIBE_CODING_COURSE_ID);

  const [projects, setProjects] = useState<Project[]>([]);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [loadingFiles, setLoadingFiles] = useState(false);
  
  const [isProjectModalOpen, setIsProjectModalOpen] = useState(false);
  const [isFileModalOpen, setIsFileModalOpen] = useState(false);
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);

  const [activeFile, setActiveFile] = useState<ProjectFile | null>(null);
  
  const [isRestoring, setIsRestoring] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        // 1. 获取项目列表
        const res = await getProjects();
        if (!isMounted) return;
        const projectList = Array.isArray(res) ? res : (res as any).data || [];
        setProjects(projectList);

        // 2. 检查 URL 意图
        const urlProjectId = searchParams.get('project');
        const urlFileId = searchParams.get('file');

        console.log("🔍 URL Params:", { project: urlProjectId, file: urlFileId });

        if (urlProjectId) {
          const pid = parseInt(urlProjectId);
          // 加载项目详情
          const detailRes = await getProjectDetail(pid);
          if (!isMounted) return;
          const detailData = (detailRes as any).data || detailRes;
          setCurrentProject(detailData);

          // 3. 只有当项目加载成功，才尝试恢复文件
          if (urlFileId) {
            const fid = parseInt(urlFileId);
            // 注意：这里必须用 detailData.files，不能依赖外部 currentProject
            const targetFile = detailData.files?.find((f: ProjectFile) => f.id === fid);
            
            if (targetFile) {
              console.log("✅ File Restored:", targetFile.name);
              setActiveFile(targetFile);
            } else {
              console.error("❌ File not found in project:", fid);
            }
          }
        } else {
          // 如果没有项目ID，重置状态
          setCurrentProject(null);
          setActiveFile(null);
        }
      } catch (err) {
        console.error("Init Error:", err);
      } finally {
        if (isMounted) {
          // 🔥 无论成功失败，必须解除 Loading 锁
          // 加一点点延迟，防止闪烁
          setTimeout(() => setIsRestoring(false), 300); 
        }
      }
    };

    // 只有在组件首次加载时设置isRestoring为true
    // 当URL参数变化时，不要重新设置isRestoring，避免显示加载界面
    if (isRestoring) {
      init();
    } else {
      // 当URL参数变化时，只更新状态，不显示加载界面
      const urlProjectId = searchParams.get('project');
      const urlFileId = searchParams.get('file');

      if (urlProjectId) {
        const pid = parseInt(urlProjectId);
        // 加载项目详情
        getProjectDetail(pid).then(detailRes => {
          const detailData = (detailRes as any).data || detailRes;
          setCurrentProject(detailData);

          if (urlFileId) {
            const fid = parseInt(urlFileId);
            const targetFile = detailData.files?.find((f: ProjectFile) => f.id === fid);
            if (targetFile) {
              setActiveFile(targetFile);
            }
          }
        }).catch(err => {
          console.error("Error loading project detail:", err);
        });
      } else {
        setCurrentProject(null);
        setActiveFile(null);
      }
    }

    return () => { isMounted = false; };
  }, [searchParams, isRestoring]); // 依赖searchParams和isRestoring

  // --- Handlers ---

  const handleSelectProject = async (projectId: number) => {
    setSearchParams({ project: projectId.toString() });
    setActiveFile(null);
    setLoadingFiles(true);
    try {
      const res = await getProjectDetail(projectId);
      setCurrentProject((res as any).data || res);
    } finally {
      setLoadingFiles(false);
    }
  };

  const handleCreateProject = async (name: string, description?: string) => {
    try {
      await createProject({ name, description });
      const res = await getProjects();
      setProjects(Array.isArray(res) ? res : (res as any).data || []);
    } catch (err) { alert("Error"); }
  };

  const handleCreateFile = async (name: string) => {
    if (!currentProject) return;
    let fileName = name;
    if (!fileName.endsWith('.html')) fileName += '.html';
    try {
      await createFile(currentProject.id, { name: fileName, file_type: 'html' });
      const res = await getProjectDetail(currentProject.id);
      setCurrentProject((res as any).data || res);
    } catch (err) { alert("Error"); }
  };

  const handleRefreshProjects = async () => {
    const res = await getProjects();
    const projectList = Array.isArray(res) ? res : (res as any).data || [];
    setProjects(projectList);
    
    if (currentProject && !projectList.find((p: Project) => p.id === currentProject.id)) {
      setCurrentProject(null);
      setActiveFile(null);
      setSearchParams({});
    }
  };

  const handleRefreshFiles = async () => {
    if (currentProject) {
      const res = await getProjectDetail(currentProject.id);
      const detailData = (res as any).data || res;
      setCurrentProject(detailData);
      
      if (activeFile && !detailData.files?.find((f: ProjectFile) => f.id === activeFile.id)) {
        setActiveFile(null);
      }
    }
  };

  const handleOpenFile = (file: ProjectFile) => {
    setActiveFile(file);
    setSearchParams({ 
      project: currentProject?.id.toString() || '', 
      file: file.id.toString() 
    });
  };

  const handleCloseEditor = () => {
    setActiveFile(null);
    if (currentProject) {
      setSearchParams({ project: currentProject.id.toString() });
    } else {
      setSearchParams({});
    }
  };

  // --- 渲染逻辑 ---

  // 0. 权限检查中
  if (permission.loading) {
    return (
      <div className="h-screen w-screen bg-[#0c0c0e] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-700 border-t-[#00F3FF]"></div>
          <span className="text-gray-500 font-mono text-sm">CHECKING PERMISSION...</span>
        </div>
      </div>
    );
  }

  // 1. 无权限访问
  if (!permission.hasAccess) {
    return (
      <div className="h-screen w-screen bg-[#0c0c0e] flex items-center justify-center">
        <div className="flex flex-col items-center gap-6 max-w-md text-center px-6">
          <div className="text-6xl">🚫</div>
          <h1 className="text-2xl font-bold text-white">无访问权限</h1>
          <p className="text-gray-400">
            您没有访问 VibeCoding 页面的权限。请联系管理员获取相关课程的学习权限。
          </p>
          <button
            onClick={() => navigate('/student/portal')}
            className="px-6 py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white font-medium transition-all"
          >
            返回课程列表
          </button>
        </div>
      </div>
    );
  }

  // 🔥 2. 如果正在恢复状态，显示全屏 Loading，不渲染任何子组件
  if (isRestoring) {
    return (
      <div className="h-screen w-screen bg-[#0c0c0e] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-gray-700 border-t-[#00F3FF]"></div>
          <span className="text-gray-500 font-mono text-sm">RESTORING WORKSPACE...</span>
        </div>
      </div>
    );
  }

  // 2. 状态恢复完成后，如果 activeFile 有值，直接进 Workspace
  if (activeFile) {
    return (
      <Workspace 
        file={activeFile} 
        onBack={handleCloseEditor}
      />
    );
  }

  // 3. 否则渲染文件列表
  return (
    <div className="h-screen w-screen bg-[#0c0c0e] text-white flex overflow-hidden font-sans selection:bg-purple-500/30">
      
      {/* Avatar Button */}
      <div 
        onClick={() => setIsAvatarModalOpen(true)}
        className="fixed top-4 left-4 z-50 flex cursor-pointer items-center gap-2 rounded-full border border-white/10 bg-black/40 px-3 py-1.5 backdrop-blur-md transition-all hover:border-[#00F3FF] hover:bg-black/60 group"
      >
        <div className="relative h-8 w-8 overflow-hidden rounded-full border border-gray-500 group-hover:border-[#00F3FF]">
          {user?.avatar_url ? (
             <img src={user.avatar_url} alt="User" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-gray-800 text-xs font-bold text-gray-400">
              {user?.username?.[0]?.toUpperCase() || 'U'}
            </div>
          )}
        </div>
        <span className="text-xs font-medium text-gray-300 group-hover:text-[#00F3FF]">
          {user?.username || 'Guest'}
        </span>
      </div>

      {/* Logo */}
      <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-40 flex items-center justify-center">
        <div className="bg-white rounded-lg p-2">
          <img src={logo} alt="Logo" className="h-12 w-auto" />
        </div>
      </div>

      <ProjectSidebar 
        projects={projects}
        selectedProjectId={currentProject?.id || null}
        onSelectProject={handleSelectProject}
        onCreateProject={() => setIsProjectModalOpen(true)}
        onProjectUpdated={handleRefreshProjects}
        onProjectDeleted={handleRefreshProjects}
      />

      <FileGrid 
        project={currentProject}
        loading={loadingFiles}
        onOpenFile={handleOpenFile}
        onCreateFile={() => setIsFileModalOpen(true)}
        onFileUpdated={handleRefreshFiles}
        onFileDeleted={handleRefreshFiles}
      />

      <CreateModal 
        title="新建项目"
        placeholder="例如：我的个人主页"
        isOpen={isProjectModalOpen}
        onClose={() => setIsProjectModalOpen(false)}
        onSubmit={handleCreateProject}
      />

      <CreateModal 
        title="新建网页文件"
        placeholder="例如：about.html"
        isOpen={isFileModalOpen}
        onClose={() => setIsFileModalOpen(false)}
        onSubmit={handleCreateFile}
      />

      <AvatarModal 
        isOpen={isAvatarModalOpen} 
        onClose={() => setIsAvatarModalOpen(false)} 
      />
    </div>
  );
};

export default VibeCodingPage;
